import { bindable, autoinject } from 'aurelia-framework';
import * as moment from 'moment';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { Payment } from '../../models/customer';
import { PaymentHistory } from '../../models/payment-history';
import { PaymentAccountDisplayName } from '../../models/payment-account-display-name';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';

@autoinject
export class PaymentHistoryPage {
	payments: Payment[] = null;
	paymentAccountDisplayNames: PaymentAccountDisplayName[];
	lastPaymentDate: moment.Moment = null;
	error: string;
	isLoading: boolean;
	sortBy: string = 'paymentDate';
	sortDirection: string = 'descending';

	currentPage: number = 1;
	totalItems: number = 0;

	readonly pageSizeNumbers: number[] = [5, 10, 25, 50];

	constructor(private payrazrService: PayrazrService, private globalState: State, private i18n: I18N, private router: Router) {
		this.error = null;
		this.isLoading = true;
	}

	attached() {
		this.refresh();
	}

	refresh() {
		this.isLoading = true;
		this.payrazrService.refreshGlobalCustomer()
			// this call in here in dashboard keeps the globalstate fresh in the Portal from user actions performed on children views by user
			// ... references of globalState from children views get the globalState from local storage.
			.then((state: State) => {
				this.globalState = state;
				this.loadRecentPayments();
			});
	}

	loadRecentPayments() {
		if (this.globalState && this.globalState.customer && this.globalState.accounts) {
			this.payments = [];
			this.getPaymentHistory(this.globalState.customer.id);
			this.globalState.associatedCustomers.forEach(customer => {
				this.getPaymentHistory(customer.id);
			});
		}

	}

	getPaymentHistory(customerId: string) {
		return this.payrazrService.getPaymentHistory(customerId)
			.then((pastPayments: PaymentHistory[]) => {
				let payments = pastPayments.map(payment => new Payment(payment));

				// set bucket key for each payment. The query for payments doesn't include bucket key,
				// but it should be safe to just map it based on current customer's customer
				// accounts (i.e., they won't have dupe account numbers)
				payments.forEach((p: Payment) => {
					let accountName = p.accountName;
					if (accountName.includes(",")) {
						accountName = (accountName.split(",")[0]).trim();
					}
					let customerAccountForPayment = this.globalState.accounts.find(x => x.displayName === accountName);
					p.bucketKey = customerAccountForPayment.bucket ? customerAccountForPayment.bucket.bucketKey : customerAccountForPayment.bucketKey || null;
				});

				this.globalState.payments = payments;
				this.payments = this.payments.concat(payments.sort((a: Payment, b: Payment) => {
					const aDate = moment(a.paymentDate);
					const bDate = moment(b.paymentDate);

					if (aDate.isBefore(bDate)) {
						return 1;
					} else if (aDate.isSame(bDate)) {
						return 0;
					} else {
						return -1;
					}

				}));
				this.totalItems = this.payments.length;
				this.lastPaymentDate = null;
				this.getDisplayNames();
				this.isLoading = false;
			})
			.catch(error => {
				this.error = this.i18n.i18next.t('unableToRetrievePaymentHistory', 'Unable to retrieve payment history. Please refresh the page to retry.');
				this.isLoading = false;
			});
	}

	getDisplayNames() {
		if (this.payments && this.payments.length !== 0) {
			this.paymentAccountDisplayNames = [];
			this.getLastPaymentDate();

			this.payments.forEach((item: Payment) => {
				let accountName = item.accountName;
				if (accountName.includes(",")) {
					accountName = (accountName.split(",")[0]).trim();
				}
				let account = this.globalState.accounts.find(f => f.displayName === accountName);
				let portfolio = this.globalState.business.fetchPortfolioSettings(account.portfolioName);
				this.paymentAccountDisplayNames.push(
					new PaymentAccountDisplayName(
						item.accountName,
						this.payrazrService.referenceIdWrapper(item.accountName, '-', portfolio.merchantSettings.thirdPartyIntegrationSettings.platform)
					));
			}, this);
		}
	}

	getLastPaymentDate() {
		if (this.payments.length !== 0) {
			this.payments.forEach((item: Payment) => {
				if (item.result === 'Approved' || item.result === 'Declined' || item.result === 'Error' || item.result === 'Submitted' || item.result === 'Returned') {
					let m: moment.Moment = moment(item.paymentDate);
					if (!this.lastPaymentDate) {
						this.lastPaymentDate = m;
					}
					else if (this.lastPaymentDate && this.lastPaymentDate < m) {
						this.lastPaymentDate = m;
					}
				}
			});
		}
	}

	sort() {
		this.sortDirection = (this.sortDirection === 'ascending') ? 'descending' : 'ascending';
	}

	getAccountDisplayName(referenceId: string): string {
		if (!this.paymentAccountDisplayNames) {
			return this.i18n.i18next.t('unavailable', 'Unavailable');
		}
		let foundAccountDisplayName: PaymentAccountDisplayName = this.paymentAccountDisplayNames
			.find((item: PaymentAccountDisplayName) => item.referenceId === referenceId);
		return (foundAccountDisplayName) ? foundAccountDisplayName.displayName : this.i18n.i18next.t('unavailable', 'Unavailable');
	}

	goToReceipt(payment: Payment) {
		let receiptRoute = this.router.routes.find(r => r.name === 'paymentHistoryReceipt');
		receiptRoute.payment = payment;
		this.router.navigate('payment-history/receipt');
	}
}
